/**
 * cart-client — de enige plek die met de winkelwagen van Shopify praat.
 *
 * Volgt de UMD-vorm van `assets/cart-helpers.js`: de browser krijgt
 * `window.cartClient`, `node --test` laadt hem met `require()`. Geen
 * afhankelijkheden buiten Node's ingebouwde modules, zodat de PR-gate hem
 * zonder install-stap kan draaien.
 */
(function (root, factory) {
  var client = factory();

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = client;
  }

  if (root) {
    root.cartClient = client;
  }
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  /** De line item-eigenschap die een plant, pot en opvulmateriaal aan elkaar knoopt. */
  var CONFIGURATION_PROPERTY = '_configuration_id';

  /** Het event dat na elke geslaagde mutatie afgaat, met de nieuwe winkelwagen erin. */
  var CART_CHANGED_EVENT = 'cart:changed';

  /**
   * De tekst die de koper ziet wanneer Shopify geen reden meegeeft én de beller
   * geen vertaling heeft. Bewust Nederlands: dit is UI-tekst, geen logregel.
   */
  var CART_ERROR_FALLBACK = 'Er is iets misgegaan. Probeer het opnieuw.';

  function lineItems(cart) {
    return cart && Array.isArray(cart.items) ? cart.items : [];
  }

  function configurationIdOf(item) {
    var properties = item && item.properties;
    if (!properties) return '';

    var value = properties[CONFIGURATION_PROPERTY];
    if (value == null) return '';

    return String(value).trim();
  }

  /**
   * De compositie-fan-out: welke regelsleutels gaan mee bij een mutatie?
   *
   * Een losse regel muteert alleen zichzelf. Een regel die bij een configuratie
   * hoort trekt de hele configuratie mee, in de volgorde waarin de winkelwagen
   * hem teruggeeft. Een regel met een eigen aantal (pot of opvulmateriaal via de
   * plus- en minknoppen) blijft alleen: die beslissing kent de winkelwagen niet,
   * dus geeft de beller hem mee als `independentQuantity`.
   *
   * @param {{items?: Array<{key?: string, properties?: Object}>}|null} cart
   * @param {string} key regelsleutel van de regel waarop geklikt is
   * @param {{independentQuantity?: boolean}} [options]
   * @returns {string[]} de te muteren regelsleutels; leeg als de regel niet bestaat
   */
  function resolveMutationKeys(cart, key, options) {
    var items = lineItems(cart);
    var target = null;

    for (var i = 0; i < items.length; i += 1) {
      if (items[i] && items[i].key === key && key) {
        target = items[i];
        break;
      }
    }

    if (!target) return [];
    if (options && options.independentQuantity) return [target.key];

    var configurationId = configurationIdOf(target);
    if (!configurationId) return [target.key];

    return items
      .filter(function (item) {
        return item && item.key && configurationIdOf(item) === configurationId;
      })
      .map(function (item) {
        return item.key;
      });
  }

  /**
   * De besturingselementen die een regel in de drawer kan dragen
   * (`snippets/block-cartitem.liquid`). `deleteConfirm` en `deleteCancel` staan
   * er wel in, maar overleven een verversing nooit: de bevestiging is een
   * klasse (`cart-item--delete-mode`) die JavaScript zet, en de sectie-render
   * levert de regel altijd zonder die klasse terug.
   */
  var LINE_CONTROLS = ['increase', 'decrease', 'quantity', 'delete', 'deleteConfirm', 'deleteCancel'];

  /** Wat er na een verversing nog bestaat van het element dat focus had. */
  function survivingControl(control) {
    if (LINE_CONTROLS.indexOf(control) === -1) return 'delete';
    if (control === 'deleteConfirm' || control === 'deleteCancel') return 'delete';

    return control;
  }

  function indexOfKey(cart, key) {
    var items = lineItems(cart);

    for (var i = 0; i < items.length; i += 1) {
      if (items[i] && items[i].key === key) return i;
    }

    return -1;
  }

  /**
   * Welk element moet focus krijgen nadat de drawer opnieuw is opgebouwd?
   *
   * De verversing wisselt de hele `innerHTML` om, dus het element dat focus had
   * bestaat daarna niet meer en de focus valt terug op `<body>`. Vóór de wissel
   * legt de drawer vast wélke regel het was en wélke knop; deze functie zegt
   * daarna waar de focus heen moet. Ze raakt de DOM niet — de aanroeper vertaalt
   * het doel naar een selector.
   *
   * De regels, in volgorde:
   * 1. Was er geen focus in de drawer, dan verplaatsen we niets (`none`). Focus
   *    afpakken van iets buiten de drawer is erger dan hem laten staan.
   * 2. Is de winkelwagen leeg geworden, dan is er geen regel meer om aan te
   *    wijzen. De lege drawer bevat alleen een melding en de sluitknop, dus
   *    wijst hij de sluitknop aan (`close`): dat houdt de schermlezer binnen de
   *    drawer en benoemt zijn enige overgebleven actie.
   * 3. Bestaat de regel nog, dan krijgt dezelfde knop op dezelfde regel focus.
   * 4. Is de regel weg, dan krijgt de regel focus die zijn positie innam — of,
   *    als de regel de laatste was, de regel ervóór. Dat is dezelfde plek op het
   *    scherm, dus de koper hoeft niet opnieuw te mikken.
   *
   * @param {{key?: string, control?: string}|null} previousFocus
   * @param {{items?: Array<{key?: string}>}|null} newCart de winkelwagen zoals de mutatie hem teruggaf
   * @param {{items?: Array<{key?: string}>}|null} [previousCart] de winkelwagen zoals hij op het scherm stond
   * @returns {{type: 'line', key: string, control: string}|{type: 'close'}|{type: 'none'}}
   */
  function resolveFocusTarget(previousFocus, newCart, previousCart) {
    var previousKey = previousFocus && previousFocus.key ? String(previousFocus.key) : '';
    if (!previousKey) return { type: 'none' };

    var items = lineItems(newCart);
    if (!items.length) return { type: 'close' };

    var control = survivingControl(previousFocus.control);

    if (indexOfKey(newCart, previousKey) !== -1) {
      return { type: 'line', key: previousKey, control: control };
    }

    // De regel is weg. Zijn positie in de oude winkelwagen bepaalt de buur;
    // zonder oude winkelwagen is de eerste regel het enige voorspelbare doel.
    var previousIndex = indexOfKey(previousCart, previousKey);
    var neighbourIndex = previousIndex === -1 ? 0 : Math.min(previousIndex, items.length - 1);

    return { type: 'line', key: items[neighbourIndex].key, control: control };
  }

  // --- Uitgestelde spinner ---------------------------------------------------
  // Een winkelwagenmutatie is meestal binnen een paar honderd milliseconden
  // klaar. Meteen een skelet tonen levert dan een flits op die het geheel
  // trager laat voelen dan het is. Daarom twee drempels, hier als benoemde
  // constanten zodat ze niet als losse getallen door de drawer zwerven.

  /**
   * Zo lang wachten we af voordat er iets in beeld komt. Onder deze grens
   * leest een wachttijd als "direct" en heeft een spinner geen functie; alles
   * wat we dan tonen is een flits.
   */
  var SPINNER_SHOW_DELAY_MS = 200;

  /**
   * Staat de spinner er eenmaal, dan blijft hij minstens zo lang staan. Een
   * antwoord dat vlak na het tonen binnenkomt zou hem anders laten knipperen,
   * en dat leest als een storing in plaats van als voortgang.
   */
  var SPINNER_MINIMUM_VISIBLE_MS = 500;

  /**
   * De timingbeslissing rond een laadindicator, los van de DOM.
   *
   * De aanroeper levert `show` en `hide` en meldt met `start()` en `stop()`
   * wanneer het werk begint en eindigt; wanneer er iets zichtbaar wordt is aan
   * deze controller. Timers en klok zijn injecteerbaar zodat `node --test` het
   * gedrag kan doorlopen zonder echt te wachten.
   *
   * @param {{
   *   show: Function,
   *   hide: Function,
   *   showDelayMs?: number,
   *   minimumVisibleMs?: number,
   *   now?: () => number,
   *   setTimeout?: Function,
   *   clearTimeout?: Function
   * }} options
   * @returns {{start: Function, stop: Function}}
   */
  function createDeferredSpinner(options) {
    var settings = options || {};
    var show = typeof settings.show === 'function' ? settings.show : function () {};
    var hide = typeof settings.hide === 'function' ? settings.hide : function () {};
    var showDelayMs =
      typeof settings.showDelayMs === 'number' ? settings.showDelayMs : SPINNER_SHOW_DELAY_MS;
    var minimumVisibleMs =
      typeof settings.minimumVisibleMs === 'number'
        ? settings.minimumVisibleMs
        : SPINNER_MINIMUM_VISIBLE_MS;
    var now = typeof settings.now === 'function' ? settings.now : Date.now;
    var schedule = typeof settings.setTimeout === 'function' ? settings.setTimeout : setTimeout;
    var unschedule =
      typeof settings.clearTimeout === 'function' ? settings.clearTimeout : clearTimeout;

    var showTimer = null;
    var hideTimer = null;
    var shownAt = null;
    var running = false;

    function conceal() {
      hideTimer = null;
      shownAt = null;
      hide();
    }

    function start() {
      // Stond het verbergen nog gepland, dan is de spinner op dit moment
      // zichtbaar: laten staan is rustiger dan hem weghalen en direct opnieuw
      // opbouwen. De minimale zichtbaarheid loopt door vanaf het eerste tonen.
      if (hideTimer !== null) {
        unschedule(hideTimer);
        hideTimer = null;
      }

      if (running) return;
      running = true;

      if (shownAt !== null || showTimer !== null) return;

      showTimer = schedule(function () {
        showTimer = null;
        if (!running) return;
        shownAt = now();
        show();
      }, showDelayMs);
    }

    function stop() {
      running = false;

      // Nog niets getoond: de drempel is nooit gehaald, dus er valt niets weg
      // te halen. Dit is het snelle antwoord waar de koper geen spinner ziet.
      if (showTimer !== null) {
        unschedule(showTimer);
        showTimer = null;
        return;
      }

      if (shownAt === null) return;

      var remaining = minimumVisibleMs - (now() - shownAt);
      if (remaining <= 0) {
        conceal();
        return;
      }

      hideTimer = schedule(conceal, remaining);
    }

    return { start: start, stop: stop };
  }

  /**
   * De basis-URL van de winkelwagen, hergebruikt uit `cart-helpers.js` zodat die
   * op één plek blijft staan. Buiten de browser (node --test) is er geen
   * storefront-root; dan volstaat het standaardpad.
   */
  function cartRoot() {
    if (typeof window !== 'undefined' && typeof window.cartSectionUrl === 'function') {
      return window.cartSectionUrl('').split('?')[0];
    }

    return '/cart';
  }

  function dispatchCartChanged(cart) {
    if (typeof document === 'undefined' || typeof CustomEvent !== 'function') return;

    document.dispatchEvent(
      new CustomEvent(CART_CHANGED_EVENT, { detail: { cart: cart } })
    );
  }

  /**
   * Eén `POST /cart/update.js`, en het antwoord wordt uitgelezen: Shopify geeft
   * daar de volledige winkelwagen in terug. Een losse `GET /cart.js` erachteraan
   * is dus dubbel werk. Bellers luisteren naar `cart:changed`; deze module roept
   * ze niet aan.
   *
   * @param {Object<string, number>} updates regelsleutel → nieuw aantal
   * @param {{fallbackErrorMessage?: string}} [options]
   * @returns {Promise<Object>} de winkelwagen zoals Shopify hem teruggaf
   */
  async function updateCart(updates, options) {
    var settings = options || {};
    var response = await fetch(cartRoot() + '/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: updates }),
    });

    var body = null;
    try {
      body = await response.json();
    } catch (_) {}

    if (!response.ok) {
      // Laatste redmiddel, en geen overbodige zorgvuldigheid: de drawer leest
      // zijn fouttekst uit `window.translations` en houdt een lege string over
      // zodra die sleutel ontbreekt. Gooien met een lege boodschap levert dan
      // een leeg `role="alert"`-regeltje op — een rode streep zonder tekst.
      throw new Error(
        (body && body.description) || settings.fallbackErrorMessage || CART_ERROR_FALLBACK
      );
    }

    dispatchCartChanged(body);

    return body;
  }

  return {
    CART_CHANGED_EVENT: CART_CHANGED_EVENT,
    CONFIGURATION_PROPERTY: CONFIGURATION_PROPERTY,
    LINE_CONTROLS: LINE_CONTROLS,
    SPINNER_MINIMUM_VISIBLE_MS: SPINNER_MINIMUM_VISIBLE_MS,
    SPINNER_SHOW_DELAY_MS: SPINNER_SHOW_DELAY_MS,
    createDeferredSpinner: createDeferredSpinner,
    resolveFocusTarget: resolveFocusTarget,
    resolveMutationKeys: resolveMutationKeys,
    updateCart: updateCart,
  };
});
